import { forwardRef, useEffect, useRef } from 'react';
import { type ImageProps } from 'next/image';

export type UploadcareImageOperation = 'preview' | 'scale_crop';

export type UploadcareImageTransformationProps = {
  uploadcareAlignment?: string;
  uploadcareAspectRatio?: string;
  uploadcareMediaAspectRatio?: string;
  uploadcareMediaQuery?: string;
  uploadcareOperation?: UploadcareImageOperation;
};

export type ImageComponentProps = ImageProps & UploadcareImageTransformationProps;

export type ImageComponentWithoutRef = (props: ImageComponentProps) => JSX.Element;

export type ImageComponent =
  | ImageComponentWithoutRef
  | React.ForwardRefExoticComponent<ImageComponentProps & React.RefAttributes<HTMLImageElement>>;

export const DefaultImageComponent: ImageComponent = forwardRef<HTMLImageElement, ImageComponentProps>(
  (
    {
      width,
      height,
      src,
      priority,
      uploadcareAlignment: _uploadcareAlignment,
      uploadcareAspectRatio: _uploadcareAspectRatio,
      uploadcareMediaAspectRatio: _uploadcareMediaAspectRatio,
      uploadcareMediaQuery: _uploadcareMediaQuery,
      uploadcareOperation: _uploadcareOperation,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLImageElement>(null);
    const imgRef = ref || internalRef; // Use forwarded ref if provided, else use internal ref

    // Fix React issue for DOM property not being recognized
    useEffect(() => {
      if ('current' in imgRef && imgRef.current && priority) {
        imgRef.current.setAttribute('fetchpriority', 'high');
      }
    }, [priority, imgRef]);

    return <img {...props} ref={imgRef} width={width} height={height} src={src as string} />;
  },
);

DefaultImageComponent.displayName = 'DefaultImageComponent';
